// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Diff assembly: classification into added / removed / modified / moved /
 * unchanged, with noise suppression (spec §6.3).
 */

import { get, structuralEqual, type Node } from '../sexp/index.ts';
import { union, type MaybeBBox } from '../model/bbox.ts';
import type { Board, BoardObject } from '../model/board.ts';
import { diffFields, type FieldChange } from './fields.ts';
import { diffGlobal, type GlobalDiff } from './global.ts';
import { matchObjects, type MatchMethod } from './match.ts';
import { normalizeObject, type NormalizeOptions } from './normalize.ts';
import { analysePosition } from './position.ts';

export type ChangeKind = 'added' | 'removed' | 'modified' | 'moved' | 'unchanged';

export interface ObjectChange {
  kind: ChangeKind;
  /** How the two sides were paired; `none` for adds and removes. */
  matchedBy: MatchMethod | 'none';
  a?: BoardObject;
  b?: BoardObject;
  fields: FieldChange[];
  /** Anchor displacement, for the UI's arrow. Present on moves and some modifies. */
  displacement?: [number, number];
  rotation?: [number, number];
  /**
   * The only difference was the UUID (spec §6.3 noise). These are classified
   * `unchanged` and hidden unless "show metadata changes" is on.
   */
  uuidOnly: boolean;
  /** The only difference was a zone fill cache with an unchanged outline (§3.5). */
  fillCacheOnly: boolean;
  /** Where to look on the board: the union of both sides' boxes. */
  bbox: MaybeBBox;
  /** Stable identifier for UI selection and for linking violations to objects. */
  key: string;
}

export interface DiffCounts {
  added: number;
  removed: number;
  modified: number;
  moved: number;
  unchanged: number;
}

export interface DiffResult {
  a: Board;
  b: Board;
  changes: ObjectChange[];
  /** Changes excluding `unchanged` — what the user actually cares about. */
  substantive: ObjectChange[];
  counts: DiffCounts;
  /** How many pairs the geometric fallback rescued (spec §6.3). */
  geometricMatches: number;
  /** Suppressed noise, reported as a count so it is never invisible. */
  uuidOnlyChanges: number;
  fillCacheOnlyChanges: number;
  global: GlobalDiff;
}

export interface DiffOptions {
  /** Include UUID-only and fill-cache-only differences as real changes. */
  showMetadataChanges?: boolean;
}

export function diffBoards(a: Board, b: Board, options: DiffOptions = {}): DiffResult {
  const { matches, onlyA, onlyB, geometricMatches } = matchObjects(a.objects, b.objects);
  const changes: ObjectChange[] = [];
  let uuidOnlyChanges = 0;
  let fillCacheOnlyChanges = 0;

  /**
   * Group membership is stored as raw UUIDs, which a copy/paste renumbers
   * wholesale (§3.2, §3.6). Comparing a group's members meaningfully therefore
   * needs the *match* result: a member of B is named by whatever object in A it
   * was matched to. Without this, every group inside a pasted region reports as
   * modified purely because its members were renumbered.
   */
  const memberIdInA = new Map<string, string>();
  for (const { a: oa, b: ob } of matches) {
    if (ob.uuid !== undefined) memberIdInA.set(ob.uuid, oa.id);
  }
  const normalizeA: NormalizeOptions = {};
  const normalizeB: NormalizeOptions = { resolveMemberId: (uuid) => memberIdInA.get(uuid) };

  for (const o of onlyA) {
    changes.push({
      kind: 'removed',
      matchedBy: 'none',
      a: o,
      fields: [],
      uuidOnly: false,
      fillCacheOnly: false,
      bbox: o.bbox,
      key: `removed:${o.id}`,
    });
  }
  for (const o of onlyB) {
    changes.push({
      kind: 'added',
      matchedBy: 'none',
      b: o,
      fields: [],
      uuidOnly: false,
      fillCacheOnly: false,
      bbox: o.bbox,
      key: `added:${o.id}`,
    });
  }

  for (const { a: oa, b: ob, method } of matches) {
    const na = normalizeObject(oa.node, a, normalizeA);
    const nb = normalizeObject(ob.node, b, normalizeB);
    const bbox = union(oa.bbox, ob.bbox);
    const key = `matched:${oa.id}`;

    if (structuralEqual(na, nb)) {
      // Identical once normalised. Work out *why* they were not byte-identical,
      // so the metadata toggle has something to show.
      const uuidOnly = oa.uuid !== ob.uuid;
      const fillCacheOnly = zoneFillDiffers(oa.node, ob.node);
      if (uuidOnly) uuidOnlyChanges++;
      if (fillCacheOnly) fillCacheOnlyChanges++;

      if (options.showMetadataChanges && (uuidOnly || fillCacheOnly)) {
        const fields: FieldChange[] = [];
        if (uuidOnly) {
          fields.push({
            path: 'uuid',
            ...(oa.uuid !== undefined ? { before: oa.uuid } : {}),
            ...(ob.uuid !== undefined ? { after: ob.uuid } : {}),
          });
        }
        if (fillCacheOnly) fields.push({ path: 'zone fill cache', before: 'stale', after: 'recomputed' });
        changes.push({
          kind: 'modified',
          matchedBy: method,
          a: oa,
          b: ob,
          fields,
          uuidOnly,
          fillCacheOnly,
          bbox,
          key,
        });
        continue;
      }

      changes.push({
        kind: 'unchanged',
        matchedBy: method,
        a: oa,
        b: ob,
        fields: [],
        uuidOnly,
        fillCacheOnly,
        bbox,
        key,
      });
      continue;
    }

    const position = analysePosition(na, nb);
    const change: ObjectChange = {
      kind: position.translationOnly ? 'moved' : 'modified',
      matchedBy: method,
      a: oa,
      b: ob,
      fields: position.translationOnly ? [] : diffFields(na, nb),
      uuidOnly: false,
      fillCacheOnly: false,
      bbox,
      key,
    };
    if (position.displacement) change.displacement = position.displacement;
    if (position.rotation) change.rotation = position.rotation;
    changes.push(change);
  }

  // Stable, human-friendly ordering: by type, then by position on the board.
  changes.sort(compareChanges);

  const counts: DiffCounts = { added: 0, removed: 0, modified: 0, moved: 0, unchanged: 0 };
  for (const c of changes) counts[c.kind]++;

  return {
    a,
    b,
    changes,
    substantive: changes.filter((c) => c.kind !== 'unchanged'),
    counts,
    geometricMatches,
    uuidOnlyChanges,
    fillCacheOnlyChanges,
    global: diffGlobal(a, b),
  };
}

function compareChanges(x: ObjectChange, y: ObjectChange): number {
  const tx = (x.a ?? x.b)!.type;
  const ty = (y.a ?? y.b)!.type;
  if (tx !== ty) return tx < ty ? -1 : 1;
  const bx = x.bbox;
  const by = y.bbox;
  if (bx && by) {
    if (bx.minX !== by.minX) return bx.minX - by.minX;
    if (bx.minY !== by.minY) return bx.minY - by.minY;
  }
  return x.key < y.key ? -1 : x.key > y.key ? 1 : 0;
}

/** Did the zone fill cache change while the outline stayed the same? */
function zoneFillDiffers(a: Node, b: Node): boolean {
  if (a.name !== 'zone') return false;
  const fa = get(a, 'filled_polygon');
  const fb = get(b, 'filled_polygon');
  if (fa === undefined && fb === undefined) return false;
  if (fa === undefined || fb === undefined) return true;
  return !structuralEqual(fa, fb);
}

/** One-line summary of a change, used by the change list and the merge report. */
export function describeChange(c: ObjectChange, describe: (o: BoardObject) => string): string {
  const obj = c.b ?? c.a;
  const what = obj ? describe(obj) : '(unknown object)';
  switch (c.kind) {
    case 'added':
      return `+ ${what}`;
    case 'removed':
      return `− ${what}`;
    case 'moved': {
      const d = c.displacement;
      const by = d ? ` by (${d[0].toFixed(3)}, ${d[1].toFixed(3)}) mm` : '';
      return `→ ${what} moved${by}`;
    }
    case 'modified':
      return `~ ${what}`;
    case 'unchanged':
      return `= ${what}`;
  }
}
